import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import fetch from 'cross-fetch'
import { Modal, Spinner } from 'oio-react'
import CloseIcon from 'assets/icons/close'
import { CheckCircle, Warning } from '@phosphor-icons/react'
import { CommunitiesWebsiteContext } from '../context'

const reasons = [
   'Get a demo of Communities',
   'See how Communities can work for you',
   'See which plan is right for your organization'
]

const ContactModal = () => {
   const [status, setStatus] = useState('ready')
   const [error, setError] = useState('')

   const { contactModalOptions, setContactModalOptions } = useContext(CommunitiesWebsiteContext)
   const [formData, setFormData] = useState({
      firstName: '',
      lastName: '',
      email: '',
      message: ''
   })

   const handleClose = () => {
      setContactModalOptions({ open: false, reason: null })

      setTimeout(() => {
         setStatus('ready')
      }, 300)
   }

   const handleSubmit = async () => {
      setStatus('pending')
      setError('')

      const response = await fetch('/communities/contact', {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json'
         },
         body: JSON.stringify({ ...formData, reason: contactModalOptions.reason })
      })

      const result = await response.json()

      if (result.error) {
         setStatus('ready')
         setError(result.error)
      } else {
         await setTimeout(() => {
            setStatus('success')
            setFormData({
               firstName: '',
               lastName: '',
               email: '',
               message: ''
            })
         }, 1000)
      }
   }

   return (
      <Modal
         width="90%[a] 100%[b-f]"
         height="90%[a]"
         maxWidth="440px"
         borderRadius="30px"
         onCloseComplete={() => setContactModalOptions({ open: false, reason: null })}
         onCloseTrigger={() => setContactModalOptions({ open: false, reason: null })}
         open={contactModalOptions.open}>
         <div className="relative w-full h-full overflow-y-auto p-8 bg-emerald-900 text-white rounded-3xl">
            <div className={classNames(status === 'success' ? 'opacity-0' : 'opacity-100')}>
               <div className="flex items-center justify-between">
                  <div className="text-white text-2xl tracking-tighter font-semibold mt-4">
                     Get in touch with us
                  </div>
               </div>
               {error && (
                  <div className="flex items-center gap-4 py-2 px-4 rounded-md bg-amber-100 text-amber-700 text-sm mt-4">
                     <div className="flex-0">
                        <Warning size={14} weight="fill" />
                     </div>
                     <div className="flex-1">{error}</div>
                  </div>
               )}
               <div className="text-white text-sm opacity-70 mt-4">
                  {reasons.map(reason => (
                     <div key={reason} className="h-6 flex items-center gap-2">
                        <div className="pt-1">
                           <CheckCircle size={16} weight="bold" />
                        </div>
                        <div>{reason}</div>
                     </div>
                  ))}
               </div>
               <div className="grid grid-cols-2 gap-4 mt-6 border-t border-solid border-white/20 pt-6">
                  <div>
                     <div className="text-sm text-white font-semibold mb-2">First Name</div>
                     <input
                        type="text"
                        placeholder="Jane"
                        className="w-full h-8 rounded-md bg-white/10 border border-solid border-white/0 px-2 text-sm text-white placeholder:text-white/50 focus:border-white/30 transition-all duration-300"
                        value={formData.firstName}
                        onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                     />
                  </div>
                  <div>
                     <div className="text-sm text-white font-semibold mb-2">Last Name</div>
                     <input
                        type="text"
                        placeholder="Smith"
                        className="w-full h-8 rounded-md bg-white/10 border border-solid border-white/0 px-2 text-sm text-white placeholder:text-white/50 focus:border-white/30 transition-all duration-300"
                        value={formData.lastName}
                        onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                     />
                  </div>
                  <div className="col-span-2">
                     <div className="text-sm text-white font-semibold mb-2">Email</div>
                     <input
                        type="text"
                        placeholder="jane.smith@example.com"
                        className="w-full h-8 rounded-md bg-white/10 border border-solid border-white/0 px-2 text-sm text-white placeholder:text-white/50 focus:border-white/30 transition-all duration-300"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                     />
                  </div>
                  <div className="col-span-2">
                     <div className="text-sm text-white font-semibold mb-2">How can we help?</div>
                     <textarea
                        type="text"
                        className="w-full rounded-md bg-white/10 border border-solid border-white/0 p-2 text-sm text-white placeholder:text-white/50 focus:border-white/30 transition-all duration-300"
                        rows="4"
                        value={formData.message}
                        onChange={e => setFormData({ ...formData, message: e.target.value })}
                     />
                  </div>
                  <div className="col-span-2">
                     <button
                        type="button"
                        className={classNames(
                           'w-full h-12 rounded-md bg-green-300 text-sm text-black font-semibold transition-all cursor-pointer transform active:translate-y-[-2px]',
                           status === 'pending' ? 'bg-white' : 'hover:bg-white'
                        )}
                        onClick={handleSubmit}>
                        {status === 'ready' && 'Send Message'}
                        {status === 'pending' && (
                           <Spinner width="16px" height="16px" color="#004f3b" />
                        )}
                     </button>
                  </div>
               </div>
            </div>
            <div
               className={classNames(
                  'absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center text-center transition-all duration-700',
                  status === 'success'
                     ? 'top-0 opacity-100 pointer-events-auto'
                     : 'top-4 opacity-0 pointer-events-none'
               )}>
               <div className="flex justify-center py-24">
                  <div className="flex flex-col items-center gap-4">
                     <CheckCircle size={90} weight="regular" />
                     <div className="text-white text-2xl tracking-tighter font-semibold">
                        Message Sent!
                     </div>
                     <div className="text-white text-sm opacity-70">
                        Thank you for your interest.
                        <br />
                        We&apos;ll be in touch with you shortly.
                     </div>
                     <button
                        type="button"
                        className={classNames(
                           'px-8 h-12 rounded-full bg-green-300 hover:bg-white text-black font-semibold transition-all cursor-pointer transform active:translate-y-[-2px] mt-4'
                        )}
                        onClick={handleClose}>
                        Close Window
                     </button>
                  </div>
               </div>
            </div>
            <div className="absolute top-6 right-6">
               <button
                  type="button"
                  className="flex items-center justify-center text-white opacity-40 hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={handleClose}>
                  <CloseIcon width="20px" height="20px" color="white" />
               </button>
            </div>
         </div>
      </Modal>
   )
}

export default ContactModal
